import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportPreview = ({ reportType, config, onGenerate, onExport }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const mockReportData = {
    'eu-ets': {
      title: 'EU ETS Surrender Report 2024',
      summary: {
        totalEmissions: '125,847 tCO₂',
        euaRequired: '125,847 EUA',
        euaHeld: '128,000 EUA',
        surplus: '2,153 EUA',
        complianceStatus: 'Compliant'
      },
      sections: [
        { title: 'Executive Summary', pages: 2, status: 'complete' },
        { title: 'Emissions by Vessel', pages: 8, status: 'complete' },
        { title: 'EUA Transaction Records', pages: 4, status: 'complete' },
        { title: 'Verification Statement', pages: 3, status: 'pending' },
        { title: 'Appendices', pages: 12, status: 'complete' }
      ],
      vessels: [
        { name: 'MV Atlantic Voyager', emissions: '28,450 tCO₂', voyages: 24 },
        { name: 'MV Pacific Trader', emissions: '31,200 tCO₂', voyages: 28 },
        { name: 'MV Nordic Explorer', emissions: '22,890 tCO₂', voyages: 19 },
        { name: 'MV Southern Star', emissions: '25,670 tCO₂', voyages: 22 },
        { name: 'MV Eastern Wind', emissions: '17,637 tCO₂', voyages: 16 }
      ]
    },
    'fueleu': {
      title: 'FuelEU Maritime Compliance Statement 2024',
      summary: {
        ghgIntensity: '89.2 gCO₂eq/MJ',
        targetIntensity: '91.16 gCO₂eq/MJ',
        complianceBalance: '+2.1%',
        penaltyRisk: 'None',
        complianceStatus: 'Compliant'
      },
      sections: [
        { title: 'Compliance Overview', pages: 3, status: 'complete' },
        { title: 'GHG Intensity Calculations', pages: 6, status: 'complete' },
        { title: 'Fuel Consumption Data', pages: 5, status: 'complete' },
        { title: 'Port Call Records', pages: 4, status: 'complete' },
        { title: 'Alternative Fuels Usage', pages: 2, status: 'complete' }
      ],
      vessels: [
        { name: 'MV Atlantic Voyager', intensity: '87.5 gCO₂eq/MJ', status: 'Compliant' },
        { name: 'MV Pacific Trader', intensity: '91.8 gCO₂eq/MJ', status: 'At Risk' },
        { name: 'MV Nordic Explorer', intensity: '85.2 gCO₂eq/MJ', status: 'Compliant' },
        { name: 'MV Southern Star', intensity: '89.1 gCO₂eq/MJ', status: 'Compliant' },
        { name: 'MV Eastern Wind', intensity: '92.4 gCO₂eq/MJ', status: 'At Risk' }
      ]
    },
    'imo-dcs': {
      title: 'IMO Data Collection System Report 2024',
      summary: {
        totalFuelConsumption: '45,230 tonnes',
        totalCO2Emissions: '142,450 tonnes',
        totalDistance: '485,670 nautical miles',
        totalHours: '8,760 hours',
        complianceStatus: 'Submitted'
      },
      sections: [
        { title: 'Ship Information', pages: 2, status: 'complete' },
        { title: 'Fuel Consumption Data', pages: 8, status: 'complete' },
        { title: 'CO₂ Emissions', pages: 6, status: 'complete' },
        { title: 'Operational Data', pages: 4, status: 'complete' },
        { title: 'Methodology', pages: 3, status: 'complete' }
      ],
      vessels: [
        { name: 'MV Atlantic Voyager', fuel: '9,850 tonnes', emissions: '31,020 tonnes' },
        { name: 'MV Pacific Trader', fuel: '11,200 tonnes', emissions: '35,280 tonnes' },
        { name: 'MV Nordic Explorer', fuel: '8,450 tonnes', emissions: '26,620 tonnes' },
        { name: 'MV Southern Star', fuel: '9,120 tonnes', emissions: '28,730 tonnes' },
        { name: 'MV Eastern Wind', fuel: '6,610 tonnes', emissions: '20,800 tonnes' }
      ]
    },
    'cii-evidence': {
      title: 'CII Evidence Pack 2024',
      summary: {
        fleetAverageRating: 'B',
        vesselsAtRisk: '2 vessels',
        improvementRequired: '3.2% efficiency gain',
        nextReview: 'January 2025',
        complianceStatus: 'Action Required'
      },
      sections: [
        { title: 'CII Rating Overview', pages: 4, status: 'complete' },
        { title: 'Individual Vessel Ratings', pages: 10, status: 'complete' },
        { title: 'Improvement Measures', pages: 6, status: 'complete' },
        { title: 'SEEMP Part III', pages: 8, status: 'complete' },
        { title: 'Corrective Action Plan', pages: 5, status: 'complete' }
      ],
      vessels: [
        { name: 'MV Atlantic Voyager', rating: 'B', status: 'Compliant' },
        { name: 'MV Pacific Trader', rating: 'C', status: 'Monitoring' },
        { name: 'MV Nordic Explorer', rating: 'A', status: 'Excellent' },
        { name: 'MV Southern Star', rating: 'D', status: 'Action Required' },
        { name: 'MV Eastern Wind', rating: 'D', status: 'Action Required' }
      ]
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    
    // Simulate report generation
    setTimeout(() => {
      setPreviewData(mockReportData?.[reportType]);
      setIsGenerating(false);
      onGenerate();
    }, 3000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'complete': return 'text-success bg-success/10';
      case 'pending': return 'text-warning bg-warning/10';
      case 'Compliant': return 'text-success bg-success/10';
      case 'At Risk': return 'text-warning bg-warning/10';
      case 'Action Required': return 'text-error bg-error/10';
      case 'Excellent': return 'text-success bg-success/10';
      case 'Monitoring': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getComplianceIcon = (status) => {
    switch (status) {
      case 'Compliant': return 'CheckCircle';
      case 'At Risk': return 'AlertTriangle';
      case 'Action Required': return 'AlertCircle';
      case 'Excellent': return 'Award';
      case 'Monitoring': return 'Eye';
      default: return 'Circle';
    }
  };

  if (!previewData && !isGenerating) {
    return (
      <div className="maritime-card p-8 text-center">
        <Icon name="FileText" size={48} strokeWidth={1} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Generate Report Preview</h3>
        <p className="text-muted-foreground mb-6">
          Click the button below to generate a preview of your compliance report with current data.
        </p>
        <Button 
          onClick={handleGenerate}
          iconName="Play"
          iconPosition="left"
          size="lg"
        >
          Generate Preview
        </Button>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className="maritime-card p-8 text-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Generating Report...</h3>
        <p className="text-muted-foreground mb-4">
          Processing data and compiling compliance documentation
        </p>
        <div className="w-full bg-muted rounded-full h-2">
          <div className="bg-primary h-2 rounded-full animate-pulse" style={{ width: '65%' }}></div>
        </div>
        <div className="text-sm text-muted-foreground mt-2">Estimated time: 2-3 minutes</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Report Preview</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Review the generated report before export
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            onClick={() => setPreviewData(null)}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Regenerate
          </Button>
          <Button 
            onClick={onExport}
            iconName="Download"
            iconPosition="left"
          >
            Export Report
          </Button>
        </div>
      </div>
      {/* Report Header */}
      <div className="maritime-card p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-foreground mb-2">{previewData?.title}</h3>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span>Generated: {new Date()?.toLocaleDateString()}</span>
              <span>•</span>
              <span>Period: {config?.reportingPeriod || '2024'}</span>
              <span>•</span>
              <span>Jurisdiction: {config?.jurisdiction || 'EU'}</span>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(previewData?.summary?.complianceStatus)}`}>
            {previewData?.summary?.complianceStatus}
          </div>
        </div>

        {/* Summary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(previewData?.summary)?.map(([key, value]) => (
            <div key={key} className="text-center">
              <div className="text-2xl font-bold text-foreground">{value}</div>
              <div className="text-xs text-muted-foreground capitalize">
                {key?.replace(/([A-Z])/g, ' $1')?.trim()}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Report Sections */}
        <div className="maritime-card p-6">
          <h4 className="text-lg font-semibold text-foreground mb-4">Report Sections</h4>
          <div className="space-y-3">
            {previewData?.sections?.map((section, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">{index + 1}</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{section?.title}</div>
                    <div className="text-xs text-muted-foreground">{section?.pages} pages</div>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(section?.status)}`}>
                  {section?.status}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total Pages:</span>
              <span className="font-medium text-foreground">
                {previewData?.sections?.reduce((sum, section) => sum + section?.pages, 0)} pages
              </span>
            </div>
          </div>
        </div>

        {/* Vessel Details */}
        <div className="maritime-card p-6">
          <h4 className="text-lg font-semibold text-foreground mb-4">Vessel Summary</h4>
          <div className="space-y-3">
            {previewData?.vessels?.map((vessel, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon name="Ship" size={16} strokeWidth={2} className="text-primary" />
                  <div>
                    <div className="text-sm font-medium text-foreground">{vessel?.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {vessel?.emissions && `${vessel?.emissions}`}
                      {vessel?.fuel && `${vessel?.fuel} fuel`}
                      {vessel?.intensity && `${vessel?.intensity}`}
                      {vessel?.rating && `Rating: ${vessel?.rating}`}
                    </div>
                  </div>
                </div>
                {vessel?.status && (
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(vessel?.status)}`}>
                    <Icon name={getComplianceIcon(vessel?.status)} size={12} strokeWidth={2} />
                    <span>{vessel?.status}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Export Options */}
      <div className="maritime-card p-6">
        <h4 className="text-lg font-semibold text-foreground mb-4">Export Options</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button variant="outline" className="h-20 flex-col space-y-2">
            <Icon name="FileText" size={24} strokeWidth={2} />
            <span className="text-sm">PDF Report</span>
          </Button>
          <Button variant="outline" className="h-20 flex-col space-y-2">
            <Icon name="FileSpreadsheet" size={24} strokeWidth={2} />
            <span className="text-sm">Excel Data</span>
          </Button>
          <Button variant="outline" className="h-20 flex-col space-y-2">
            <Icon name="Code" size={24} strokeWidth={2} />
            <span className="text-sm">XML Export</span>
          </Button>
          <Button variant="outline" className="h-20 flex-col space-y-2">
            <Icon name="Database" size={24} strokeWidth={2} />
            <span className="text-sm">JSON Data</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;